import React, { PropsWithChildren, ReactElement, useEffect, useMemo, useRef, useState } from 'react'
import { styled } from '~/app/components'
import { FadeIn, FadeOut } from '~/app/components/common/Animations'
import useEventHooks from '~/app/components/common/useEventHooks'
import { zIndexLow, zIndexMiddle } from '~/app/components/styles/units'
import { isMobile } from 'react-device-detect'
import { useModalAnimation } from '@yanolja-org/ya-com-modal'
import { Icon_close } from '@yanolja-org/ya-com-system-icon'
import { Typography } from '@yanolja-org/ya-com-core'
/**
 * FixedTooltip component
 * author: soojin.oh
 */

type Position = Partial<Record<'top' | 'left' | 'right' | 'bottom', number>>
type Placement = 'top' | 'right' | 'bottom' | 'left'
// styled components
const Container = styled.div`
  display: inline-block;
  position: relative;
`

const Tooltip = styled.div<
  Position & {
    height?: number
    width?: number
    offset: number
    stickyTo: 'left' | 'right'
    placement: Placement
    pointHorizontalOffset?: number
    borderRadius?: number
  }
>`
  position: absolute;
  ${({ top, placement }) => {
    switch (placement) {
      // TODO case 'left', 'right'
      case 'top':
        return `bottom: ${(top || 0) + 4}px`
      case 'bottom':
        return `top: ${top}px`
    }
  }};
  ${({ stickyTo, left, right }) => (stickyTo === 'left' ? `left: ${left}px` : `right: ${right}px`)};
  background: ${({ theme }) => theme.color.black_op80};
  display: flex;
  ${({ width }) => (width ? `width: ${width}rem` : 'min-width: 28rem')};
  border-radius: ${({ borderRadius }) => (borderRadius ? `${borderRadius}rem` : '0.8rem')};
  padding: 1.2rem;
  color: ${({ theme }) => theme.color.white};
  justify-content: flex-start;
  z-index: ${zIndexLow};

  &.show {
    animation: ${FadeIn} 0.3s ease;
    animation-fill-mode: forwards;
  }

  &.hide {
    animation: ${FadeOut} 0.3s ease;
    animation-fill-mode: forwards;
  }

  & > .tail {
    position: absolute;
    width: 100%;
    // .tail 영역(width,height 모두) hover를 위한 여백
    height: calc(100% + 0.7rem);
    padding-top: 0.7rem;
    top: -0.7rem;
    left: 0;
    box-sizing: border-box;

    ::before {
      content: '';
      display: inline-block;
      position: absolute;
      border-left: 0.6rem solid transparent;
      border-right: 0.6rem solid transparent;
      ${({ stickyTo, offset }) =>
        stickyTo === 'left' ? `left: ${offset}px` : `right: ${offset}px`};
      ${({ placement, theme }) => {
        switch (placement) {
          // TODO left, right
          case 'top':
            return `bottom: -0.7rem; border-top: 0.7rem solid ${theme.color.black_op80};`
          case 'bottom':
            return `top: 0; border-bottom: 0.7rem solid ${theme.color.black_op80};`
        }
      }}
    }
  }
`

const ContentWrapper = styled.div`
  flex: 1 1 auto;
  ${Typography('body2.multiline').regular};
  text-align: left;
  z-index: ${zIndexMiddle};

  & .description {
    margin-right: 0.8rem;
    margin-bottom: 0.4rem;
  }

  & .link {
    cursor: pointer;
    ${Typography('body2.multiline').bold};
  }
`

const CloseWrapper = styled.div`
  flex: 0 1 auto;
  display: flex;
  justify-content: center;
  z-index: ${zIndexMiddle};
`
const CloseIcon = styled(Icon_close)`
  cursor: pointer;
  path {
    fill: ${({ theme }) => theme.color.white};
  }
`

export type FixedTooltipProps = {
  stickyTo?: 'left' | 'right'
  content: ReactElement
  initialShowValue?: boolean
  // tooltip 안에 close icon 포함 여부
  includeCloseIcon?: boolean
  width?: number
  height?: number
  borderRadius?: number
  /*
   * 툴팁 left, right 값 임의로 지정
   * stickyTo = left 이면 customTooltipLeft 값 설정
   * right 이면 customTooltipRight 값 설정
   */
  customTooltipLeft?: number
  customTooltipRight?: number
  // 툴팁 위치 (children 기준)
  placement?: Placement
  // 툴팁 포인터의 수평 offset
  pointHorizontalOffset?: number
  /**
   * 툴팁의 position
   * 텍스트와 툴팁 사이의 거리
   */
  tooltipOffset?: number
  childrenStyle?: React.CSSProperties
} & Pick<React.HTMLProps<HTMLDivElement>, 'style'>

// FixedTooltip component
const FixedTooltip = function ({
  children,
  stickyTo = 'left',
  content,
  style,
  childrenStyle,
  initialShowValue = false,
  includeCloseIcon = true,
  width,
  height,
  customTooltipLeft,
  customTooltipRight,
  placement = 'bottom',
  pointHorizontalOffset,
  borderRadius,
  tooltipOffset,
}: PropsWithChildren<FixedTooltipProps>) {
  const containerRef = useRef<HTMLDivElement>(null)
  const childrenRef = useRef<HTMLDivElement>(null)
  const containerParentRect = useMemo<Partial<DOMRect>>(
    () => containerRef.current?.parentElement?.getBoundingClientRect() || {},
    [containerRef.current?.parentElement?.getBoundingClientRect()]
  )
  const childrenRect = useMemo<Partial<DOMRect>>(
    () => childrenRef.current?.getBoundingClientRect() || {},
    [childrenRef.current?.getBoundingClientRect()]
  )
  const tooltipTop = useMemo(
    () => (tooltipOffset ? tooltipOffset : (childrenRect.height || 0) + 7),
    [childrenRect, tooltipOffset]
  )
  const tooltipLeft = useMemo(
    () => (containerParentRect.left || 0) - (childrenRect.left || 0) - 10,
    [childrenRect, containerParentRect]
  )

  const tooltipRight = useMemo(
    () =>
      (containerParentRect.left || 0) +
      (containerParentRect.width || 0) -
      (childrenRect.left || 0) -
      (childrenRect.width || 0) -
      10,
    [childrenRect, containerParentRect]
  )

  const offset =
    stickyTo === 'left'
      ? (childrenRect.left || 0) - (containerParentRect.left || 0)
      : (containerParentRect.left || 0) +
        (containerParentRect.width || 0) -
        (childrenRect.left || 0) -
        (childrenRect.width || 0)

  const [isShow, setShow] = useState(initialShowValue)
  const { onAnimationEnd, isDetached } = useModalAnimation({
    isOpened: isShow,
    targetId: 'tooltip',
  })

  useEffect(() => {
    if (initialShowValue) {
      return
    }
    const setTooltipShowoff = () => setShow(false)
    window.addEventListener('click', setTooltipShowoff)
    return () => window.removeEventListener('click', setTooltipShowoff)
  })
  const { stop } = useEventHooks()

  return (
    <Container
      ref={containerRef}
      onClick={stop()}
      style={style}
      onMouseOver={_ => {
        if (!isMobile) setShow(true)
      }}
      onMouseLeave={_ => {
        if (!isMobile) setShow(false)
      }}
    >
      <div
        style={childrenStyle}
        ref={childrenRef}
        onClick={_ => {
          if (isMobile) setShow(!isShow)
        }}
      >
        {children}
      </div>
      {(isShow || !isDetached) && (
        <Tooltip
          onAnimationEnd={onAnimationEnd}
          height={height}
          width={width}
          top={tooltipTop}
          left={customTooltipLeft ? customTooltipLeft * 10 : tooltipLeft}
          right={customTooltipRight ? customTooltipRight * 10 : tooltipRight}
          offset={pointHorizontalOffset ? pointHorizontalOffset * 10 : offset + 10}
          className={isShow ? 'show' : 'hide'}
          id={'tooltip'}
          stickyTo={stickyTo}
          placement={placement}
          pointHorizontalOffset={pointHorizontalOffset}
          borderRadius={borderRadius}
        >
          <div className={'tail'} />
          <ContentWrapper>{content}</ContentWrapper>
          {includeCloseIcon && (
            <CloseWrapper onClick={_ => setShow(false)}>
              <CloseIcon size={1.6} unit="rem" />
            </CloseWrapper>
          )}
        </Tooltip>
      )}
    </Container>
  )
}

export default FixedTooltip
